import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, query, where, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import '../App.css';

export default function AdminTipsPage() {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTip, setSelectedTip] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [adminNotes, setAdminNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch tips from Firestore
  const fetchTips = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let tipsQuery = collection(db, 'tips');

      // Apply filters if not "all"
      if (filterStatus !== 'all') {
        tipsQuery = query(tipsQuery, where('status', '==', filterStatus));
      }
      if (filterCategory !== 'all') {
        tipsQuery = query(tipsQuery, where('category', '==', filterCategory));
      }
      if (filterPriority !== 'all') {
        tipsQuery = query(tipsQuery, where('priority', '==', filterPriority));
      }

      tipsQuery = query(tipsQuery, orderBy('createdAt', 'desc'));

      const snapshot = await getDocs(tipsQuery);
      const tipsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setTips(tipsData);
    } catch (err) {
      console.error('Error fetching tips:', err);
      setError('Failed to load tips. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory, filterPriority]);

  useEffect(() => {
    fetchTips();
  }, [fetchTips]);

  // Update tip status
  const updateTipStatus = async (tipId, newStatus) => {
    try {
      const tipRef = doc(db, 'tips', tipId);
      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };

      if (newStatus === 'read') {
        updateData.readBy = auth.currentUser?.uid;
        updateData.readAt = serverTimestamp();
      }
      if (newStatus === 'resolved') {
        updateData.resolvedBy = auth.currentUser?.uid;
        updateData.resolvedAt = serverTimestamp();
      }
      if (newStatus === 'archived') {
        updateData.archivedAt = serverTimestamp();
      }

      await updateDoc(tipRef, updateData);

      setSuccessMessage(`Tip marked as ${newStatus}`);
      setTimeout(() => setSuccessMessage(''), 3000);

      fetchTips();
      setSelectedTip(null);
    } catch (err) {
      console.error('Error updating tip:', err);
      setError('Failed to update tip status');
    }
  };

  // Update tip priority
  const updateTipPriority = async (tipId, newPriority) => {
    try {
      const tipRef = doc(db, 'tips', tipId);
      await updateDoc(tipRef, {
        priority: newPriority,
        updatedAt: serverTimestamp()
      });

      setSuccessMessage('Priority updated');
      setTimeout(() => setSuccessMessage(''), 3000);

      fetchTips();
    } catch (err) {
      console.error('Error updating priority:', err);
      setError('Failed to update priority');
    }
  };

  // Save admin notes
  const saveAdminNotes = async (tipId) => {
    try {
      const tipRef = doc(db, 'tips', tipId);
      await updateDoc(tipRef, {
        adminNotes: adminNotes,
        updatedAt: serverTimestamp()
      });

      setSuccessMessage('Notes saved');
      setTimeout(() => setSuccessMessage(''), 3000);

      fetchTips();
      setSelectedTip(null);
      setAdminNotes('');
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Failed to save notes');
    }
  };

  // Delete tip
  const deleteTip = async (tipId) => {
    if (!window.confirm('Are you sure you want to delete this tip? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'tips', tipId));

      setSuccessMessage('Tip deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);

      fetchTips();
      setSelectedTip(null);
    } catch (err) {
      console.error('Error deleting tip:', err);
      setError('Failed to delete tip');
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return '#dc3545';
      case 'read': return '#ffc107';
      case 'resolved': return '#28a745';
      case 'archived': return '#6c757d';
      default: return '#6c757d';
    }
  };

  // Get priority badge color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'bug': return '🐛';
      case 'feature': return '✨';
      case 'feedback': return '💬';
      case 'other': return '📝';
      default: return '📝';
    }
  };

  return (
    <div className="admin-page">
      <h2>Tips & Feedback Management</h2>

      {successMessage && (
        <div style={{
          background: '#d4edda',
          border: '1px solid #c3e6cb',
          color: '#155724',
          padding: '10px',
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          {successMessage}
        </div>
      )}

      {error && (
        <div style={{
          background: '#f8d7da',
          border: '1px solid #f5c6cb',
          color: '#721c24',
          padding: '10px',
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '5px',
        marginBottom: '20px'
      }}>
        <h3>Filters</h3>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <div>
            <label>Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ marginLeft: '10px', padding: '5px' }}
            >
              <option value="all">All</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="resolved">Resolved</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div>
            <label>Category:</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{ marginLeft: '10px', padding: '5px' }}
            >
              <option value="all">All</option>
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
              <option value="feedback">Feedback</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label>Priority:</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              style={{ marginLeft: '10px', padding: '5px' }}
            >
              <option value="all">All</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <div style={{
          background: '#fff',
          padding: '15px',
          borderRadius: '5px',
          border: '1px solid #dee2e6',
          flex: '1',
          minWidth: '150px'
        }}>
          <h4>Total Tips</h4>
          <p style={{ fontSize: '24px', margin: 0 }}>{tips.length}</p>
        </div>

        <div style={{
          background: '#fff',
          padding: '15px',
          borderRadius: '5px',
          border: '1px solid #dee2e6',
          flex: '1',
          minWidth: '150px'
        }}>
          <h4>New</h4>
          <p style={{ fontSize: '24px', margin: 0, color: '#dc3545' }}>
            {tips.filter(t => t.status === 'new').length}
          </p>
        </div>

        <div style={{
          background: '#fff',
          padding: '15px',
          borderRadius: '5px',
          border: '1px solid #dee2e6',
          flex: '1',
          minWidth: '150px'
        }}>
          <h4>In Progress</h4>
          <p style={{ fontSize: '24px', margin: 0, color: '#ffc107' }}>
            {tips.filter(t => t.status === 'read').length}
          </p>
        </div>

        <div style={{
          background: '#fff',
          padding: '15px',
          borderRadius: '5px',
          border: '1px solid #dee2e6',
          flex: '1',
          minWidth: '150px'
        }}>
          <h4>Resolved</h4>
          <p style={{ fontSize: '24px', margin: 0, color: '#28a745' }}>
            {tips.filter(t => t.status === 'resolved').length}
          </p>
        </div>
      </div>

      {/* Tips List */}
      {loading ? (
        <p>Loading tips...</p>
      ) : tips.length === 0 ? (
        <p>No tips found matching the selected filters.</p>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {tips.map(tip => (
            <div
              key={tip.id}
              style={{
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: '5px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                setSelectedTip(tip);
                setAdminNotes(tip.adminNotes || '');
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 10px 0' }}>
                    {getCategoryIcon(tip.category)} {tip.subject}
                  </h3>

                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <span style={{
                      background: getStatusColor(tip.status),
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>
                      {tip.status.toUpperCase()}
                    </span>

                    <span style={{
                      background: getPriorityColor(tip.priority),
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>
                      {tip.priority.toUpperCase()}
                    </span>

                    <span style={{
                      background: '#6c757d',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>
                      {tip.category.toUpperCase()}
                    </span>
                  </div>

                  <p style={{ color: '#495057', marginBottom: '10px' }}>
                    {tip.message.length > 200 ? tip.message.substring(0, 200) + '...' : tip.message}
                  </p>

                  <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    <p style={{ margin: '5px 0' }}>
                      <strong>From:</strong> {tip.userName} ({tip.userEmail})
                    </p>
                    <p style={{ margin: '5px 0' }}>
                      <strong>Date:</strong> {formatDate(tip.createdAt)}
                    </p>
                    {tip.adminNotes && (
                      <p style={{ margin: '5px 0' }}>
                        <strong>Admin Notes:</strong> {tip.adminNotes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedTip && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2>{getCategoryIcon(selectedTip.category)} {selectedTip.subject}</h2>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <span style={{
                background: getStatusColor(selectedTip.status),
                color: 'white',
                padding: '5px 10px',
                borderRadius: '5px'
              }}>
                {selectedTip.status.toUpperCase()}
              </span>

              <span style={{
                background: getPriorityColor(selectedTip.priority),
                color: 'white',
                padding: '5px 10px',
                borderRadius: '5px'
              }}>
                {selectedTip.priority.toUpperCase()}
              </span>

              <span style={{
                background: '#6c757d',
                color: 'white',
                padding: '5px 10px',
                borderRadius: '5px'
              }}>
                {selectedTip.category.toUpperCase()}
              </span>
            </div>

            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
              <h4>Message</h4>
              <p style={{ whiteSpace: 'pre-wrap' }}>{selectedTip.message}</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p><strong>From:</strong> {selectedTip.userName} ({selectedTip.userEmail})</p>
              <p><strong>User ID:</strong> {selectedTip.userId || 'Anonymous'}</p>
              <p><strong>Created:</strong> {formatDate(selectedTip.createdAt)}</p>
              {selectedTip.readAt && <p><strong>Read:</strong> {formatDate(selectedTip.readAt)}</p>}
              {selectedTip.resolvedAt && <p><strong>Resolved:</strong> {formatDate(selectedTip.resolvedAt)}</p>}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4>Admin Notes</h4>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '5px',
                  border: '1px solid #ced4da',
                  minHeight: '100px'
                }}
                placeholder="Add notes about this tip..."
              />
              <button
                onClick={() => saveAdminNotes(selectedTip.id)}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Save Notes
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4>Update Status</h4>
              <div style={{ display: 'flex', gap: '10px' }}>
                {selectedTip.status !== 'read' && (
                  <button
                    onClick={() => updateTipStatus(selectedTip.id, 'read')}
                    style={{
                      padding: '8px 16px',
                      background: '#ffc107',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Mark as Read
                  </button>
                )}
                {selectedTip.status !== 'resolved' && (
                  <button
                    onClick={() => updateTipStatus(selectedTip.id, 'resolved')}
                    style={{
                      padding: '8px 16px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Mark as Resolved
                  </button>
                )}
                {selectedTip.status !== 'archived' && (
                  <button
                    onClick={() => updateTipStatus(selectedTip.id, 'archived')}
                    style={{
                      padding: '8px 16px',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Archive
                  </button>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4>Update Priority</h4>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => updateTipPriority(selectedTip.id, 'high')}
                  style={{
                    padding: '8px 16px',
                    background: selectedTip.priority === 'high' ? '#dc3545' : '#f8f9fa',
                    color: selectedTip.priority === 'high' ? 'white' : 'black',
                    border: '1px solid #dc3545',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  High
                </button>
                <button
                  onClick={() => updateTipPriority(selectedTip.id, 'medium')}
                  style={{
                    padding: '8px 16px',
                    background: selectedTip.priority === 'medium' ? '#ffc107' : '#f8f9fa',
                    color: selectedTip.priority === 'medium' ? 'white' : 'black',
                    border: '1px solid #ffc107',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Medium
                </button>
                <button
                  onClick={() => updateTipPriority(selectedTip.id, 'low')}
                  style={{
                    padding: '8px 16px',
                    background: selectedTip.priority === 'low' ? '#28a745' : '#f8f9fa',
                    color: selectedTip.priority === 'low' ? 'white' : 'black',
                    border: '1px solid #28a745',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Low
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => deleteTip(selectedTip.id)}
                style={{
                  padding: '10px 20px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Delete Tip
              </button>

              <button
                onClick={() => {
                  setSelectedTip(null);
                  setAdminNotes('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}